import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { getApiUrl } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

export function BackendStatusAlert() {
  const [status, setStatus] = useState<'checking' | 'online' | 'offline' | 'dismissed'>('checking');
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    checkBackendStatus();
  }, []);

  const checkBackendStatus = async () => {
    try {
      const response = await fetchWithTimeout(
        getApiUrl(`/health`),
        {
          timeout: 5000,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );

      if (response.ok) {
        setStatus('online');
        // Don't show alert if backend is working
        setShowAlert(false);
      } else {
        setStatus('offline');
        setShowAlert(true);
      }
    } catch (error) {
      setStatus('offline');
      setShowAlert(true);
    }
  };

  const handleDismiss = () => {
    setStatus('dismissed');
    setShowAlert(false);
  };

  // Don't render if dismissed or online
  if (!showAlert || status === 'dismissed' || status === 'online') {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {status === 'checking' ? (
              <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-sm text-yellow-900 mb-1">
              {status === 'checking' ? 'Checking Backend...' : 'Backend Not Deployed'}
            </h3>
            <p className="text-xs text-yellow-800 leading-relaxed">
              {status === 'checking' 
                ? 'Verifying backend server connection...'
                : 'The CMS backend is not responding. Default content is being displayed. All data is safe - manual deployment may be required.'}
            </p>
          </div>

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-yellow-600 hover:text-yellow-800 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}